import { get, writable } from "svelte/store";

import GeoHelper from "../../Helpers/GeoHelper";
import ValidationHelper from "../../Helpers/ValidationHelper";
import StringHelper from "../../Helpers/StringHelper";

import LoadState from "../../Enums/LoadState";
import DownsampleRate from "../../Enums/DownsampleRate";
import VesselIDType from "../../Models/Enums/VesselIDType";
import VesselGroup from "../../Models/VesselGroup";

import MapStore from "../../Map/Store";
import MapDataSource from "../../Map/Enums/MapDataSource";

import User from "../../Branding/User";
import MapLayer from "../../Map/Enums/MapLayer";
import { Layout, Paint } from "../../Map/MapLayerStyles";
import MapLayerManager from "../../Map/MapLayerManager";
import MapLayerGroup from "../../Map/Enums/MapLayerGroup";

const VesselHistoryStore = {
	meta: {
		loadState: writable(LoadState.INIT),
		progress: writable(0),
		error: writable(""),
		lastUpdated: writable(0),
		mapInitted: writable(false)
	},
	vesselGroup: new VesselGroup(),
	// vessels: writable([]),
	query: {
		inputType: writable(VesselIDType.IMO),
		inputString: writable(""),
		inputValid: writable(false),
		inputLength: writable(0), // length of imos
		inputError: writable(false), // showValidationText

		fromDatetime: writable(new Date()),
		toDatetime: writable(new Date()),
		vesselInputs: writable([]),
		invalidInputs: writable([]),
		downsampling: writable(DownsampleRate.DAILY),

		limitedInputs: writable({
			[VesselIDType.IMO]: {},
			[VesselIDType.MMSI]: {}
		})
	},
	filters: {
		layers: {
			heatmap: {
				enabled: writable(true)
			},
			positions: {
				enabled: writable(true)
			},
			journeys: {
				enabled: writable(false)
			},
			vessels: {
				enabled: writable(true)
			}
		},
		selected: {
			spireIds: writable([])
		},
		filterString: writable("")
	},
	results: {
		tableData: {
			raw: writable([]),
			filtered: writable([])
		},
		points: {
			raw: writable([]),
			filtered: writable([])
		},
		lines: {
			raw: writable([]),
			filtered: writable([])
		},
		mergedPoints: {
			raw: writable([])
		}
	},
	actions: {
		initMap: (map) => {
			if (get(VesselHistoryStore.meta.mapInitted)) {
				return;
			}
			map.addSource(MapDataSource.FLEET_HISTORY, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.FLEET_HISTORY_LINES, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.HIGHLIGHT_LINES, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.HISTORIC_SNAPSHOT, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});

			// map.setStyle(Styles.Heatmap);
			map.addLayer(
				{
					id: MapLayer.HEATMAP,
					type: "heatmap",
					source: MapDataSource.FLEET_HISTORY,
					maxzoom: 9,
					paint: Paint.heatmap
				},
				"waterway-label"
			);
			map.addLayer(
				{
					id: MapLayer.HEATMAP_LINES,
					type: "line",
					source: MapDataSource.FLEET_HISTORY_LINES,
					paint: Paint.vesselTrails
				},
				"waterway-label"
			);
			map.addLayer(
				{
					id: MapLayer.HEATMAP_POINTS,
					type: "symbol",
					source: MapDataSource.FLEET_HISTORY,
					minzoom: 7,
					layout: Layout.historic_query
				},
				"waterway-label"
			);
			map.addLayer(
				{
					id: MapLayer.HIGHLIGHT_LINES,
					type: "line",
					source: MapDataSource.HIGHLIGHT_LINES,
					paint: Paint.trailsHighlighted
				},
				"waterway-label"
			);
			// map.setStyle(Styles.Alerts);

			map.addLayer(
				{
					id: MapLayer.VESSEL_HISTORY_SNAPSHOT,
					source: MapDataSource.HISTORIC_SNAPSHOT,
					type: "symbol",
					layout: Layout.vessel /* { // Layout.historic_query, Layout.vessel
						visibility: "visible", // get(MapStore.sanctionsVisible) ? "visible" : "none",
						"icon-image": "vessel-white-standard",
						"icon-allow-overlap": true,
						"icon-rotation-alignment": "map",
						"icon-rotate": { type: "identity", property: "course" },
						"icon-size": [
							"interpolate", ["linear"],
							["zoom"],
							2, 0.25,
							6, 0.5,
							10, 1
						]
					}*/
				},
				"waterway-label"
			);

			MapLayerManager.addLayerGroup(MapLayerGroup.VESSEL_HISTORY, [
				MapLayer.HEATMAP,
				MapLayer.HEATMAP_LINES,
				MapLayer.HEATMAP_POINTS,
				MapLayer.HIGHLIGHT_LINES,
				MapLayer.VESSEL_HISTORY_SNAPSHOT
			], [
				MapDataSource.FLEET_HISTORY,
				MapDataSource.FLEET_HISTORY_LINES,
				MapDataSource.HIGHLIGHT_LINES,
				MapDataSource.HISTORIC_SNAPSHOT
			]);

			VesselHistoryStore.meta.mapInitted.set(true);
		},
		deinitMap: () => {
			if (!get(VesselHistoryStore.meta.mapInitted)) {
				return;
			}
			MapLayerManager.removeLayerGroup(MapLayerGroup.VESSEL_HISTORY);
			VesselHistoryStore.meta.mapInitted.set(false);
		},
		resetMeta: () => {
			VesselHistoryStore.meta.loadState.set(LoadState.INIT);
			VesselHistoryStore.meta.progress.set(0);
			VesselHistoryStore.meta.error.set("");
			VesselHistoryStore.meta.lastUpdated.set(0);
		},
		resetQuery: () => {
			VesselHistoryStore.query.fromDatetime.set(new Date());
			VesselHistoryStore.query.toDatetime.set(new Date());
			VesselHistoryStore.query.vesselInputs.set([]);
			VesselHistoryStore.query.downsampling.set(DownsampleRate.DAILY);

			VesselHistoryStore.actions.setupLimitedInputs();
		},
		resetResults: () => {
			VesselHistoryStore.results.tableData.raw.set([]);
			VesselHistoryStore.results.points.raw.set([]);
			VesselHistoryStore.results.lines.raw.set([]);

			VesselHistoryStore.results.tableData.filtered.set([]);
			VesselHistoryStore.results.points.filtered.set([]);
			VesselHistoryStore.results.lines.filtered.set([]);
		},
		resetAll: () => {
			VesselHistoryStore.actions.resetMeta();
			VesselHistoryStore.actions.resetQuery();
			VesselHistoryStore.actions.resetResults();
		},

		setupLimitedInputs: (imosOn = [], mmsisOn = []) => {
			const selectedTogglesByType = get(VesselHistoryStore.query.limitedInputs);
			const setupForType = (type, enabled, oneLength) => {
				const list = User.vhRestrictedInputs(type);
				if (list.length === 0) {
					selectedTogglesByType[type] = {};
				}
				else {
					for (let i = 0; i < list.length; i++) {
						selectedTogglesByType[type][list[i]] = (enabled.indexOf(list[i]) >= 0);
					}
				}
			};
			setupForType(VesselIDType.IMO, imosOn, 7);
			setupForType(VesselIDType.MMSI, mmsisOn, 9);
			VesselHistoryStore.query.limitedInputs.set(selectedTogglesByType);
		},

		setFilterString: (query) => {
			VesselHistoryStore.filters.filterString.set(query);
		},
		updateMapSources: () => {
			const map = get(MapStore.mapbox.instance);
			const points = get(VesselHistoryStore.results.points.filtered);
			const lines = get(VesselHistoryStore.results.lines.filtered);

			if (!get(VesselHistoryStore.meta.mapInitted)) {
				console.log("🔄 Refreshing VH sources REJECTED (no map layers)");
				return;
			}

			const pointsGeojson = GeoHelper.emptyGeoJson(points); // console.log("pointsGeojson", pointsGeojson);
			map.getSource(MapDataSource.FLEET_HISTORY).setData(pointsGeojson);
			map.getSource(MapDataSource.FLEET_HISTORY_LINES).setData(GeoHelper.emptyGeoJson(lines));

			const vessels = get(VesselHistoryStore.vesselGroup.vessels);
			const vesselsDisplay = [];
			vessels.forEach((vessel) => {
				vessel.updateSnapshot();
				// if (vessel.visible) {
				vesselsDisplay.push(vessel.currentFeature);
				// }
			});
			// console.log("vesselsDisplay", vesselsDisplay);
			map.getSource(MapDataSource.HISTORIC_SNAPSHOT).setData(GeoHelper.emptyGeoJson(vesselsDisplay));
		}
	},
	helpers: {
		shouldShowLoadingCard: () => {
			console.log("shouldShowLoadingCard");
			return get(VesselHistoryStore.meta.loadState) === LoadState.LOADING &&
					get(VesselHistoryStore.meta.progress) > 0.0;
		},
		shouldShowLoadingSpinner: () => {
			return get(VesselHistoryStore.meta.loadState) === LoadState.LOADING &&
				get(VesselHistoryStore.meta.error) === "";
		},
		updateInputsFromToggles: () => {
			let newTextAreaValue = "";
			let count = 0;
			const switches = document.querySelectorAll("#vessel-history-components .inputs .mdc-switch__native-control");
			for (let i = 0; i < switches.length; i++) {
				const checked = switches[i].checked;
				if (checked) {
					newTextAreaValue += switches[i].getAttribute("label");
					count++;
					if (i !== switches.length - 1) {
						newTextAreaValue += ",";
					}
				}
			}
			console.log("newTextAreaValue", newTextAreaValue);
			VesselHistoryStore.helpers.onImoMMSITextareaInputValue(newTextAreaValue);

			VesselHistoryStore.query.inputValid.set(count > 0);
			VesselHistoryStore.query.inputLength.set(count);
			// VesselHistoryStore.query.inputString
		},
		updateTogglesFromInputs: () => {
			const inputs = get(VesselHistoryStore.query.vesselInputs);
			const switches = document.querySelectorAll("#vessel-history-components .inputs .mdc-switch__native-control");
			for (let i = 0; i < switches.length; i++) {
				const label = switches[i].getAttribute("label");
				console.log("label", label, inputs);
				if ((inputs.indexOf(label) >= 0)) {
					switches[i].checked = true;
					// switches[i].parentElement.parentElement.parentElement.classList.add("mdc-switch--checked");
					console.log("added class", label);
				}
				else {
					switches[i].checked = false;
					// switches[i].parentElement.parentElement.parentElement.classList.remove("mdc-switch--checked");
					console.log("removed class", label);
				}
			}
		},
		selectAllInputs: () => {
			const switches = document.querySelectorAll("#vessel-history-components .inputs .mdc-switch__native-control");
			for (let i = 0; i < switches.length; i++) {
				switches[i].checked = true;
				switches[i].parentElement.parentElement.parentElement.classList.add("mdc-switch--checked");
			}
			VesselHistoryStore.helpers.updateInputsFromToggles();
		},
		deselectAllInputs: () => {
			const switches = document.querySelectorAll("#vessel-history-components .inputs .mdc-switch__native-control");
			for (let i = 0; i < switches.length; i++) {
				switches[i].checked = false;
				switches[i].parentElement.parentElement.parentElement.classList.remove("mdc-switch--checked");
			}
			VesselHistoryStore.helpers.updateInputsFromToggles();
		},

		// Called on input in imo text area
		onImoMMSITextareaInput: (event) => {
			console.log("onImoMMSITextareaInput", event);
			VesselHistoryStore.helpers.onImoMMSITextareaInputValue(event.target.value);
		},
		onImoMMSITextareaInputValue: (newValue) => {
			const inputType = get(VesselHistoryStore.query.inputType);
			let allValuesValid = true; // Default to true
			VesselHistoryStore.query.invalidInputs.set([]);
			if (newValue.length > 0) {
				// don't try parse empty
				let splitValues = [];
				if (inputType === VesselIDType.IMO && newValue.length >= 7) {
					splitValues = StringHelper.splitIMOsByCommaOrSpace(newValue);
				}
				else if (inputType === VesselIDType.MMSI && newValue.length >= 9) {
					splitValues = StringHelper.splitMMSIsByCommaOrSpace(newValue);
				}
				if (splitValues.length > 0) {
					// if there's something other than commas and spaces...
					console.log(splitValues);
					const invalids = [];
					for (let i = 0; i < splitValues.length; i++) {
						const val = splitValues[i];
						if ((inputType === VesselIDType.IMO && !ValidationHelper.isValidIMO(val)) ||
							(inputType === VesselIDType.MMSI && !ValidationHelper.isValidMMSI(val))
						) {
							// set back to false if any don't match
							allValuesValid = false;
							VesselHistoryStore.query.inputError.set(true); // showValidationText = true;

							invalids.push(val);
						}
					}
					VesselHistoryStore.query.invalidInputs.set(invalids);
					if (allValuesValid) {
						VesselHistoryStore.query.vesselInputs.set(splitValues); // All is good, set in store
						VesselHistoryStore.query.inputLength.set(splitValues.length);
					}
				}
				else {
					VesselHistoryStore.query.invalidInputs.update(existing => [
						...existing,
						newValue
					]);
					allValuesValid = false;
				}
			}
			VesselHistoryStore.query.inputValid.set(allValuesValid);
			if (allValuesValid) {
				VesselHistoryStore.query.inputError.set(false); // showValidationText = false;
			}
		},

		// Used to show validation text if user leaves invalid number
		// in input without nagging on partial completion
		onImoMMSITextareaLoseFocus: () => {
			console.log("onImoMMSITextareaLoseFocus");
			if (!get(VesselHistoryStore.query.inputValid) && get(VesselHistoryStore.query.inputLength) > 0) {
				VesselHistoryStore.query.inputError.set(true); // showValidationText = true;
			}
		}
	}

};
export default VesselHistoryStore;
